/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { ServiceIcon } from '../ServiceIcon';
import { ServiceIconNumeric } from './ServiceIconNumeric';

type LazyloadOffset = number | number[] | null;
export type Service = {
  name: string;
  src: string;
};

type ServiceIconsProps<T extends Service> = {
  /**
   * Label to render for screenreaders.
   */
  ariaLabel?: string;
  /**
   * @deprecated - Previously used by `Img` for the `react-lazyload` library.
   * We are now using a native browser-level lazyload so this no longer does
   * anything.
   *
   * Indicates whether the icons should lazily load. If `undefined`
   * then it'll default to `Img`'s `lazyloadOffset`.
   */
  lazyloadOffset?: LazyloadOffset;
  /**
   * Maxmium number of service icons to render. If more are provided, the final "icon" will be a number representing the number of unrendered services.
   */
  maxServices: number;
  /**
   * An array of service icons to render.
   *
   * - `name` is the name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   * - `src` is a URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  services: Array<T>;
  /**
   * Component to render the service icons
   */
  renderServiceIcon: (
    service: T,
    props: ServiceIconsProps<T>
  ) => React.ReactNode;
};

const Styles = labelStyles('ServiceIcons', {
  root: {
    display: 'flex',
    userSelect: 'none' as 'none',
  },
  item: {
    marginLeft: -2,
  },
});

/**
 * Renders icons representing loose collection of services (aka apps, aka
 * integrations).
 *
 * The aesthetics of this component are intended to convey that the services
 * are related, but not as part of a Zap. For rendering the services or steps
 * in a Zap, use [`ZapServiceIcons`](/#/Patterns/ZapServiceIcons).
 */
export const ServiceIcons = <T extends Service>(
  props: ServiceIconsProps<T>
) => {
  const { maxServices, services, renderServiceIcon } = props;
  const label = props.ariaLabel || `${services.length} apps mentioned`;
  const remainder = Math.min(services.length - maxServices, 99);
  const hasExtraServices = remainder > 0;
  const sliceIndex = hasExtraServices ? maxServices - 1 : maxServices;

  // Using `idx` as `key` since the same `service` could potentially
  // appear multiple times and therefore would not be unique.
  const icons = services.slice(0, sliceIndex).map((service, idx) => (
    <li css={Styles.item} key={idx}>
      {renderServiceIcon(service, props)}
    </li>
  ));

  if (hasExtraServices) {
    icons.push(
      <li css={Styles.item} key={`remainder-${remainder + 1}`}>
        <ServiceIconNumeric number={remainder + 1} />
      </li>
    );
  }

  return (
    <ul aria-label={label} css={Styles.root}>
      {icons}
    </ul>
  );
};

function defaultRenderServiceIcon({ name, src }: Service) {
  return <ServiceIcon serviceName={name} size={40} src={src} />;
}

ServiceIcons.defaultProps = {
  maxServices: 6,
  renderServiceIcon: defaultRenderServiceIcon,
};
